import { isVideoFrameReady } from '@/Pages/Selfie/helpers/captureVideoFrame'

/**
 * Abrir a câmera sem que "não abriu de primeira" chegue ao convidado.
 *
 * Em campo, parte dos celulares não abria a câmera na primeira tentativa e só
 * funcionava depois de recarregar a página. As causas conhecidas:
 *
 * - logo depois do prompt de permissão, alguns Android respondem
 *   `NotReadableError`/`AbortError` ("Could not start video source"): o
 *   hardware ainda não soltou a câmera;
 * - o detector do Amplify abre um stream, falha numa checagem seguinte (15 fps,
 *   troca de `deviceId`) e NUNCA para esse primeiro stream — a câmera fica presa
 *   até a página descarregar, e é por isso que o refresh "resolvia";
 * - o `<video>` recebe o stream e nunca começa a tocar (iOS), sem erro e sem
 *   timeout — skeleton eterno.
 *
 * A defesa, em camadas: repetir o `getUserMedia` quando o erro é passageiro,
 * resolver a permissão ANTES do detector (ele passa a abrir a câmera já
 * liberada, como depois de um refresh), vigiar o vídeo até o primeiro quadro e,
 * se ainda assim travar, reabrir na página e, por último, recarregá-la uma vez.
 */

/**
 * Em teste só as ESPERAS encolhem — quantas tentativas e o que dispara cada
 * passo continuam iguais (mesmo recurso de `basedata/fetchWithRetry.ts`).
 */
export const CAMERA_TIMINGS =
  import.meta.env.MODE === 'test'
    ? {
        acquireBackoffMs: [1, 1, 1],
        acquireDeadlineMs: 20,
        releaseMs: 1,
        watchdogTickMs: 10,
        playNudgeMs: 20,
        startTimeoutMs: 60,
        permissionGraceMs: 200,
        maxRestarts: 1,
      }
    : {
        /** Esperas entre tentativas de `getUserMedia` com erro passageiro. */
        acquireBackoffMs: [400, 1_000, 2_000],
        /** Com a permissão já concedida, `getUserMedia` que passa disto travou. */
        acquireDeadlineMs: 8_000,
        /** Folga para o hardware soltar a câmera antes de outra abertura. */
        releaseMs: 400,
        watchdogTickMs: 500,
        /** Vídeo parado a esta altura leva um `play()` (autoplay travado no iOS). */
        playNudgeMs: 2_500,
        /** Sem o primeiro quadro até aqui, a câmera travou. */
        startTimeoutMs: 8_000,
        /** Prompt de permissão aberto não conta como travado — até este limite. */
        permissionGraceMs: 30_000,
        /** Reaberturas na própria página antes de recarregá-la. */
        maxRestarts: 1,
      }

/**
 * Câmera frontal na resolução que o detector usa, mas só com `ideal`: aqui a
 * ideia é liberar a câmera, não validar requisito — um `min` recusado viraria
 * `OverconstrainedError` num aparelho que o detector aceitaria.
 */
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
  audio: false,
}

export type CameraFailureKind = 'denied' | 'not_found' | 'transient'

export type CameraAcquireResult =
  | { ok: true; stream: MediaStream }
  | { ok: false; kind: CameraFailureKind; error: unknown }

export type CameraRestartReason = 'stalled' | 'camera_error' | 'track_ended' | 'warmup_failed'

export type CameraRestartInfo = {
  reason: CameraRestartReason
  attempt: number
  /** Nome do erro do navegador (ex.: `NotReadableError`), quando houve um. */
  errorName: string | null
  /** true quando o próximo passo é recarregar a página, não reabrir nela. */
  reloading: boolean
}

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

export function cameraErrorName(error: unknown): string | null {
  if (error !== null && typeof error === 'object' && 'name' in error && typeof error.name === 'string') {
    return error.name
  }
  return null
}

/**
 * Só vale repetir o que é passageiro. Permissão negada ou aparelho sem câmera
 * não mudam na próxima tentativa — insistir só atrasaria a tela de ajuda.
 * Nomes antigos (`PermissionDeniedError`, `DevicesNotFoundError`) ainda
 * aparecem em webview de Android velho.
 */
export function classifyCameraError(error: unknown): CameraFailureKind {
  switch (cameraErrorName(error)) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'not_found'
    default:
      return 'transient'
  }
}

export function stopMediaStream(stream: MediaStream): void {
  stream.getTracks().forEach((track) => {
    try {
      track.stop()
    } catch {
      /* track já encerrada */
    }
  })
}

function hasLiveVideoTrack(stream: MediaStream): boolean {
  return stream.getVideoTracks().some((track) => track.readyState === 'live')
}

async function cameraPermissionState(): Promise<PermissionState | 'unknown'> {
  try {
    const status = await navigator.permissions.query({ name: 'camera' as PermissionName })
    return status.state
  } catch {
    // Firefox e Safari antigo não conhecem 'camera' na Permissions API.
    return 'unknown'
  }
}

class CameraAcquireTimeoutError extends Error {
  constructor() {
    super('getUserMedia did not settle with camera permission already granted')
    this.name = 'CameraAcquireTimeout'
  }
}

/**
 * `getUserMedia` que não pode ficar pendurado para sempre. O prazo só corta
 * quando a permissão JÁ está concedida: com o prompt aberto, o convidado está
 * decidindo e a espera é legítima. O stream que chegar depois do corte é
 * fechado para não prender a câmera.
 */
async function getUserMediaWithDeadline(
  mediaDevices: MediaDevices,
  constraints: MediaStreamConstraints,
): Promise<MediaStream> {
  const request = mediaDevices.getUserMedia(constraints)
  for (;;) {
    const settled = await Promise.race([
      request.then((stream) => ({ stream })),
      wait(CAMERA_TIMINGS.acquireDeadlineMs).then(() => null),
    ])
    if (settled !== null) return settled.stream
    if ((await cameraPermissionState()) === 'granted') {
      void request.then(stopMediaStream, () => undefined)
      throw new CameraAcquireTimeoutError()
    }
  }
}

/**
 * Abre a câmera repetindo o que é passageiro (câmera ocupada logo depois do
 * prompt, vídeo sem track viva). `isCancelled` interrompe as esperas quando a
 * tela já desistiu desta abertura.
 */
export async function acquireCameraStream(
  isCancelled: () => boolean = () => false,
): Promise<CameraAcquireResult> {
  const mediaDevices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  if (mediaDevices === undefined || typeof mediaDevices.getUserMedia !== 'function') {
    // Contexto sem HTTPS ou navegador sem WebRTC: não há o que repetir.
    return { ok: false, kind: 'not_found', error: new Error('getUserMedia unavailable') }
  }

  const backoff = CAMERA_TIMINGS.acquireBackoffMs
  let lastError: unknown = null

  for (let attempt = 0; attempt <= backoff.length; attempt++) {
    try {
      const stream = await getUserMediaWithDeadline(mediaDevices, CAMERA_CONSTRAINTS)
      if (hasLiveVideoTrack(stream)) return { ok: true, stream }
      stopMediaStream(stream)
      lastError = new Error('Camera stream without a live video track')
    } catch (error) {
      lastError = error
      const kind = classifyCameraError(error)
      if (kind !== 'transient') return { ok: false, kind, error }
    }

    const delay = backoff[attempt]
    if (delay === undefined || isCancelled()) break
    await wait(delay)
    if (isCancelled()) break
  }

  return { ok: false, kind: 'transient', error: lastError }
}

/**
 * Abre a câmera, confirma que ela respondeu e SOLTA — antes de o detector do
 * Amplify abrir a dele. Assim o prompt de permissão (e o tropeço do hardware
 * logo depois dele) acontece aqui, onde há retentativa, e o detector encontra a
 * câmera já liberada: a mesma condição de quem recarrega a página.
 */
export async function warmUpCamera(
  isCancelled: () => boolean,
): Promise<{ ok: true } | { ok: false; kind: CameraFailureKind; error: unknown }> {
  const result = await acquireCameraStream(isCancelled)
  if (!result.ok) return result
  stopMediaStream(result.stream)
  await wait(CAMERA_TIMINGS.releaseMs)
  return { ok: true }
}

export function hasEndedVideoTrack(video: HTMLVideoElement): boolean {
  const source = video.srcObject
  if (typeof MediaStream === 'undefined' || !(source instanceof MediaStream)) return false
  return source.getVideoTracks().some((track) => track.readyState === 'ended')
}

/** `play()` que não derruba nada: autoplay recusado é tratado pelo vigia. */
export function playVideo(video: HTMLVideoElement): void {
  try {
    void Promise.resolve(video.play()).catch(() => undefined)
  } catch {
    /* navegador sem suporte a play() programático */
  }
}

type WatchdogOptions = {
  getVideo: () => HTMLVideoElement | null
  /** Desde quando há um `getUserMedia` sem resposta (prompt aberto), ou null. */
  getPendingSince?: () => number | null
  onStall: (reason: 'stalled' | 'track_ended') => void
}

/**
 * Vigia a câmera até o primeiro quadro. Tela escondida (app em segundo plano)
 * e prompt de permissão aberto não contam no prazo. Antes de desistir, dá um
 * `play()` no vídeo parado — autoplay que o iOS travou costuma voltar com isso.
 * Devolve a função que para o vigia; ele também para sozinho ao disparar.
 */
export function startCameraWatchdog({
  getVideo,
  getPendingSince,
  onStall,
}: WatchdogOptions): () => void {
  let elapsed = 0
  let nudged = false

  const timer = window.setInterval(() => {
    if (document.visibilityState === 'hidden') return
    const pendingSince = getPendingSince?.() ?? null
    if (pendingSince !== null && Date.now() - pendingSince < CAMERA_TIMINGS.permissionGraceMs) {
      return
    }

    const video = getVideo()
    if (video !== null && isVideoFrameReady(video)) {
      window.clearInterval(timer)
      return
    }

    elapsed += CAMERA_TIMINGS.watchdogTickMs

    if (video !== null && hasEndedVideoTrack(video)) {
      window.clearInterval(timer)
      onStall('track_ended')
      return
    }
    if (!nudged && elapsed >= CAMERA_TIMINGS.playNudgeMs && video?.srcObject && video.paused) {
      nudged = true
      playVideo(video)
    }
    if (elapsed >= CAMERA_TIMINGS.startTimeoutMs) {
      window.clearInterval(timer)
      onStall('stalled')
    }
  }, CAMERA_TIMINGS.watchdogTickMs)

  return () => {
    window.clearInterval(timer)
  }
}

const CAMERA_RELOAD_KEY = 'sonny.selfie.cameraReloadAt'
/** Uma recarga automática por janela — nunca um loop de refresh. */
const CAMERA_RELOAD_WINDOW_MS = 5 * 60_000

/**
 * Reserva a recarga automática da página, se ainda não houve uma na janela.
 * Sem sessionStorage (aba privada que recusa) não há como garantir que não
 * vire loop, então não recarrega.
 */
function claimCameraReload(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(CAMERA_RELOAD_KEY))
    if (last > 0 && now - last < CAMERA_RELOAD_WINDOW_MS) return false
    window.sessionStorage.setItem(CAMERA_RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

/**
 * Próximo passo depois de a câmera falhar em abrir: reabrir na própria página;
 * esgotadas as reaberturas, recarregar a página UMA vez (o que o convidado
 * fazia à mão); depois disso, desistir — quem chama mostra a ajuda.
 */
export function nextCameraRecovery(
  restartsSoFar: number,
  now: number = Date.now(),
): 'restart' | 'reload' | 'give_up' {
  if (restartsSoFar < CAMERA_TIMINGS.maxRestarts) return 'restart'
  return claimCameraReload(now) ? 'reload' : 'give_up'
}

/**
 * O Amplify lembra a última câmera usada (`deviceId` no localStorage) e a pede
 * de novo na abertura seguinte. Depois de uma falha, esse id é suspeito —
 * esquecê-lo deixa o navegador escolher a câmera frontal do zero.
 */
export function forgetLastSelectedCamera(): void {
  try {
    window.localStorage.removeItem('AmplifyLivenessCameraId')
  } catch {
    /* storage indisponível: o Amplify segue com `ideal`, não trava */
  }
}

export function reloadPage(): void {
  window.location.reload()
}
